import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Card from 'pages/HehmDevice/HehmDashboard/Card'
import { HehmDeviceEquipment } from 'types/HehmDevice'
import { LabelAndValue } from 'pages/HehmDevice/HehmDashboard/ConfigurationAndComponentsTabContent/LabelAndValue'
import { mapToDropdownOptions, isValidNumericInput, getErrorMessage } from 'helpers/formatters'
import ConfigDropdown from 'pages/Device/SystemConfigPanel/ConfigDropdown'
import {
  HEHM_INDOOR_HEAT_TYPE,
  HEHM_INDOOR_UNIT_TYPE,
  HEHM_STAGES
} from 'helpers/reportFriendlyNames'
import ConfigInput from 'pages/Device/SystemConfigPanel/ConfigInput'
import ConfigTextInput from './ConfigTextInput'
import NoDataValue from '../NoDataValue'

type Props = {
  hehmDeviceEquipment: HehmDeviceEquipment | null
  isEditing: boolean
  onChangeCallback: (indoor: Partial<HehmDeviceEquipment['indoor']>) => void
  onValidationChange?: (hasErrors: boolean) => void
}

const isFurnace = (type: string | null | undefined) =>
  type?.toLowerCase().trim() === 'furnace'

const IndoorUnitCard = ({ hehmDeviceEquipment, isEditing, onChangeCallback, onValidationChange }: Props) => {
  const { indoor } = hehmDeviceEquipment || {}

  const [unitType, setUnitType] = useState<string | null>(indoor?.type ?? null)
  const [heatType, setHeatType] = useState<string | null>(indoor?.heatType ?? null)
  const [stages, setStages] = useState<number | string | null>(indoor?.stages ?? 'null')
  const [tonnage, setTonnage] = useState<number | null>(indoor?.tonnage ?? null)
  const [heatingCapacity, setHeatingCapacity] = useState<number | null>(
    indoor?.heatingCapacity ?? null
  )
  const [modelNumber, setModelNumber] = useState(indoor?.modelNumber ?? '')
  const [serialNumber, setSerialNumber] = useState(indoor?.serialNumber ?? '')
  const [manufacturer, setManufacturer] = useState(indoor?.manufacturer ?? '')

  const handleUnitTypeChange = (value: string | number | null) => {
    const newUnitType = value as string | null
    const newHeatType = isFurnace(newUnitType) ? 'gas' : null
    setUnitType(newUnitType)
    setHeatType(newHeatType)
    onChangeCallback({ type: newUnitType, heatType: newHeatType })
  }

  useEffect(() => {
    if (!isEditing) {
      setUnitType(indoor?.type ?? null)
      setHeatType(indoor?.heatType ?? null)
      setStages(indoor?.stages ?? 'null')
      setTonnage(indoor?.tonnage ?? null)
      setHeatingCapacity(indoor?.heatingCapacity ?? null)
      setModelNumber(indoor?.modelNumber ?? '')
      setSerialNumber(indoor?.serialNumber ?? '')
      setManufacturer(indoor?.manufacturer ?? '')
    }

    const requiresHeatType = !isFurnace(unitType)
    onValidationChange?.(!unitType || !stages || (requiresHeatType && !heatType))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hehmDeviceEquipment, isEditing, unitType, heatType, stages, onValidationChange])

  return (
    <Card title="Indoor Unit" icon="/img/indoor_unit.svg">
      <S.CardContent>
        <S.Column>
          <LabelAndValue
            label="Unit Type"
            isRequired
            value={
              !isEditing && !unitType ? (
                <NoDataValue />
              ) : (
                <ConfigDropdown
                  isEditing={isEditing}
                  value={unitType}
                  options={mapToDropdownOptions(HEHM_INDOOR_UNIT_TYPE)}
                  onChangeCallback={handleUnitTypeChange}
                  errorMessage={getErrorMessage('Unit Type', unitType)}
                />
              )
            }
          />
          <LabelAndValue
            label="Heat Type"
            isRequired
            value={
              !isEditing && !heatType ? (
                <NoDataValue />
              ) : (
                <ConfigDropdown
                  isEditing={isEditing && !isFurnace(unitType)}
                  value={heatType}
                  options={mapToDropdownOptions(HEHM_INDOOR_HEAT_TYPE)}
                  onChangeCallback={(value) => {
                    setHeatType(value as string | null)
                    onChangeCallback({ heatType: value as string | null })
                  }}
                  errorMessage={getErrorMessage('Heat Type', heatType)}
                />
              )
            }
          />
          <LabelAndValue
            label="Unit Stages"
            isRequired
            value={
              !isEditing && !stages ? (
                <NoDataValue />
              ) : (
                <ConfigDropdown
                  isEditing={isEditing}
                  value={stages}
                  options={mapToDropdownOptions(HEHM_STAGES)}
                  onChangeCallback={(value) => {
                    setStages(value as number | string | null)
                    onChangeCallback({ stages: value === 'null' ? null : value as number | string | null })
                  }}
                  errorMessage={getErrorMessage('Unit Stages', stages)}
                />
              )
            }
          />
          <LabelAndValue
            label="Indoor Unit/Coil Refrigerant Tonnage"
            value={
              !isEditing && !tonnage ? (
                <NoDataValue />
              ) : (
                <ConfigInput
                  isEditing={isEditing}
                  value={tonnage}
                  onChangeCallback={(value) => {
                    if (value === null || isValidNumericInput(String(value), 2)) {
                      setTonnage(value as number | null)
                      onChangeCallback({ tonnage: value as number | null })
                    }
                  }}
                />
              )
            }
          />
          {isFurnace(unitType) && (
            <LabelAndValue
              label="Gas Furnace Input K/BTU"
              value={
                !isEditing && !heatingCapacity ? (
                  <NoDataValue />
                ) : (
                  <ConfigInput
                    isEditing={isEditing}
                    value={heatingCapacity}
                    onChangeCallback={(value) => {
                      if (value === null || isValidNumericInput(String(value), 3)) {
                        setHeatingCapacity(value as number | null)
                        onChangeCallback({ heatingCapacity: value as number | null })
                      }
                    }}
                  />
                )
              }
            />
          )}
        </S.Column>

        <S.Column>
          <LabelAndValue
            label="Model Number"
            value={
              !isEditing && !modelNumber ? (
                <NoDataValue />
              ) : (
                <ConfigTextInput
                  isEditing={isEditing}
                  value={modelNumber}
                  errorMessage={modelNumber.length > 50 ? 'Maximum 50 characters' : undefined}
                  onChangeCallback={(value) => {
                    setModelNumber(value)
                    onChangeCallback({ modelNumber: value || null })
                  }}
                />
              )
            }
          />
          <LabelAndValue
            label="Serial Number"
            value={
              !isEditing && !serialNumber ? (
                <NoDataValue />
              ) : (
                <ConfigTextInput
                  isEditing={isEditing}
                  value={serialNumber}
                  errorMessage={serialNumber.length > 50 ? 'Maximum 50 characters' : undefined}
                  onChangeCallback={(value) => {
                    setSerialNumber(value)
                    onChangeCallback({ serialNumber: value || null })
                  }}
                />
              )
            }
          />
          <LabelAndValue
            label="Manufacturer"
            value={
              !isEditing && !manufacturer ? (
                <NoDataValue />
              ) : (
                <ConfigTextInput
                  isEditing={isEditing}
                  value={manufacturer}
                  maxLength={50}
                  onChangeCallback={(value) => {
                    setManufacturer(value)
                    onChangeCallback({ manufacturer: value || null })
                  }}
                />
              )
            }
          />
        </S.Column>
      </S.CardContent>
    </Card>
  )
}

export default IndoorUnitCard

const S = {
  CardContent: styled.div`
    display: flex;
    gap: 25px;
  `,
  Column: styled.div`
    display: flex;
    flex-direction: column;
    width: 50%;
  `
}
